/**
 * Color Role - Convert Pillar
 *
 * Conversão entre variantes de cor usando hub-and-spoke (via rgb_object/RGBA).
 *
 * @example
 * import { convertColor, hexToRgba, rgbaToHex } from '@attrx/role-morphic/color/convert';
 *
 * convertColor('hex', 'rgb_object', '#ff0000');  // { r: 255, g: 0, b: 0, a: 1 }
 * convertColor('rgb_object', 'hsl_string', { r: 255, g: 0, b: 0 }); // "hsl(0, 100%, 50%)"
 */

import type { ColorVariant, RGBA, ColorRgbObject, ColorHslObject } from "./types";
import { rgbToHsl, hslToRgb, normalizeAlpha, normalizeRgbChannel, clamp } from "./types";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

export const COLOR_BASE: ColorVariant = "rgb_object";

export const COLOR_VARIANTS: ColorVariant[] = [
  "hex",
  "rgb_object",
  "rgb_string",
  "hsl_object",
  "hsl_string",
];

// =============================================================================
// TO BASE (→ RGBA)
// =============================================================================

/**
 * Converte hex para RGBA
 */
export function hexToRgba(hex: string): RGBA {
  let h = hex.trim();
  if (h.startsWith("#")) h = h.slice(1);

  let r: number, g: number, b: number, a: number;

  if (h.length === 3) {
    r = parseInt(h[0] + h[0], 16);
    g = parseInt(h[1] + h[1], 16);
    b = parseInt(h[2] + h[2], 16);
    a = 1;
  } else if (h.length === 4) {
    r = parseInt(h[0] + h[0], 16);
    g = parseInt(h[1] + h[1], 16);
    b = parseInt(h[2] + h[2], 16);
    a = parseInt(h[3] + h[3], 16) / 255;
  } else if (h.length === 6) {
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
    a = 1;
  } else if (h.length === 8) {
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
    a = parseInt(h.slice(6, 8), 16) / 255;
  } else {
    throw new Error(`Invalid hex color: ${hex}`);
  }

  return { r, g, b, a };
}

/**
 * Converte RGB object para RGBA
 */
export function rgbObjectToRgba(rgb: ColorRgbObject): RGBA {
  return {
    r: normalizeRgbChannel(rgb.r),
    g: normalizeRgbChannel(rgb.g),
    b: normalizeRgbChannel(rgb.b),
    a: normalizeAlpha(rgb.a),
  };
}

/**
 * Converte RGB string para RGBA
 */
export function rgbStringToRgba(rgb: string): RGBA {
  const match = rgb.match(/rgba?\s*\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)/i);
  if (!match) {
    throw new Error(`Invalid RGB string: ${rgb}`);
  }

  return {
    r: normalizeRgbChannel(parseFloat(match[1])),
    g: normalizeRgbChannel(parseFloat(match[2])),
    b: normalizeRgbChannel(parseFloat(match[3])),
    a: match[4] !== undefined ? normalizeAlpha(parseFloat(match[4])) : 1,
  };
}

/**
 * Converte HSL object para RGBA
 */
export function hslObjectToRgba(hsl: ColorHslObject): RGBA {
  const { r, g, b } = hslToRgb(hsl.h, hsl.s, hsl.l);
  return {
    r,
    g,
    b,
    a: normalizeAlpha(hsl.a),
  };
}

/**
 * Converte HSL string para RGBA
 */
export function hslStringToRgba(hsl: string): RGBA {
  const match = hsl.match(/hsla?\s*\(\s*([\d.]+)\s*,\s*([\d.]+)%?\s*,\s*([\d.]+)%?\s*(?:,\s*([\d.]+)\s*)?\)/i);
  if (!match) {
    throw new Error(`Invalid HSL string: ${hsl}`);
  }

  const h = parseFloat(match[1]);
  const s = parseFloat(match[2]);
  const l = parseFloat(match[3]);
  const a = match[4] !== undefined ? parseFloat(match[4]) : 1;

  const { r, g, b } = hslToRgb(h, s, l);
  return { r, g, b, a: normalizeAlpha(a) };
}

/**
 * Converte qualquer variante para RGBA (base)
 */
export function toBaseColor(variant: ColorVariant, value: unknown): RGBA {
  switch (variant) {
    case "hex":
      return hexToRgba(value as string);
    case "rgb_object":
      return rgbObjectToRgba(value as ColorRgbObject);
    case "rgb_string":
      return rgbStringToRgba(value as string);
    case "hsl_object":
      return hslObjectToRgba(value as ColorHslObject);
    case "hsl_string":
      return hslStringToRgba(value as string);
    default:
      throw new Error(`Unknown color variant: ${variant}`);
  }
}

// =============================================================================
// FROM BASE (RGBA →)
// =============================================================================

/**
 * Converte RGBA para hex
 */
export function rgbaToHex(rgba: RGBA, includeAlpha = false): string {
  const r = rgba.r.toString(16).padStart(2, "0");
  const g = rgba.g.toString(16).padStart(2, "0");
  const b = rgba.b.toString(16).padStart(2, "0");

  if (includeAlpha || rgba.a < 1) {
    const a = Math.round(rgba.a * 255)
      .toString(16)
      .padStart(2, "0");
    return `#${r}${g}${b}${a}`;
  }

  return `#${r}${g}${b}`;
}

/**
 * Converte RGBA para RGB object
 */
export function rgbaToRgbObject(rgba: RGBA): ColorRgbObject {
  if (rgba.a < 1) {
    return { r: rgba.r, g: rgba.g, b: rgba.b, a: rgba.a };
  }
  return { r: rgba.r, g: rgba.g, b: rgba.b };
}

/**
 * Converte RGBA para RGB string
 */
export function rgbaToRgbString(rgba: RGBA): string {
  if (rgba.a < 1) {
    return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;
  }
  return `rgb(${rgba.r}, ${rgba.g}, ${rgba.b})`;
}

/**
 * Converte RGBA para HSL object
 */
export function rgbaToHslObject(rgba: RGBA): ColorHslObject {
  const { h, s, l } = rgbToHsl(rgba.r, rgba.g, rgba.b);
  if (rgba.a < 1) {
    return { h, s, l, a: rgba.a };
  }
  return { h, s, l };
}

/**
 * Converte RGBA para HSL string
 */
export function rgbaToHslString(rgba: RGBA): string {
  const { h, s, l } = rgbToHsl(rgba.r, rgba.g, rgba.b);
  if (rgba.a < 1) {
    return `hsla(${h}, ${s}%, ${l}%, ${rgba.a})`;
  }
  return `hsl(${h}, ${s}%, ${l}%)`;
}

/**
 * Converte RGBA (base) para qualquer variante
 */
export function fromBaseColor(variant: ColorVariant, rgba: RGBA): unknown {
  switch (variant) {
    case "hex":
      return rgbaToHex(rgba);
    case "rgb_object":
      return rgbaToRgbObject(rgba);
    case "rgb_string":
      return rgbaToRgbString(rgba);
    case "hsl_object":
      return rgbaToHslObject(rgba);
    case "hsl_string":
      return rgbaToHslString(rgba);
    default:
      throw new Error(`Unknown color variant: ${variant}`);
  }
}

// =============================================================================
// CONVERT
// =============================================================================

/**
 * Converte entre duas variantes de cor
 *
 * @param from - Variante de origem
 * @param to - Variante de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertColor('hex', 'rgb_object', '#ff0000');
 * // { r: 255, g: 0, b: 0 }
 */
export function convertColor<TFrom = unknown, TTo = unknown>(
  from: ColorVariant,
  to: ColorVariant,
  value: TFrom,
): TTo {
  // Otimização: mesma variante
  if (from === to) {
    return value as unknown as TTo;
  }

  // Converte via base (RGBA)
  const rgba = toBaseColor(from, value);
  return fromBaseColor(to, rgba) as TTo;
}

/**
 * Versão safe de convertColor que retorna Result
 */
export function tryConvertColor<TFrom = unknown, TTo = unknown>(
  from: ColorVariant,
  to: ColorVariant,
  value: TFrom,
): Result<TTo> {
  try {
    const result = convertColor<TFrom, TTo>(from, to, value);
    return { ok: true, value: result };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Verifica se variante existe
 */
export function hasColorVariant(variant: string): variant is ColorVariant {
  return COLOR_VARIANTS.includes(variant as ColorVariant);
}

/**
 * Retorna lista de variantes
 */
export function getColorVariants(): ColorVariant[] {
  return [...COLOR_VARIANTS];
}
